import type { FullPlanOnlyFeature } from "@salon-pos/shared";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";

/** Plan & billing in the owner portal; the store app never ships an owner-portal path (check-native-bundle). */
const PLAN_PATH = import.meta.env.VITE_CLIENT === "native" ? null : "/owner/settings?tab=plan";

/**
 * (Task W) "Available on the Full plan — upgrade": shown instead of a module that is Full-plan-only (customer
 * subscriptions, staff attendance / clock-in, Task Y: own custom domain) when the salon is on Base. Owners get the upgrade button; everyone else
 * is asked to talk to the owner. Nothing is hidden silently where an owner would look.
 */
export function FullPlanUpsell({ feature, compact = false }: { feature: FullPlanOnlyFeature; compact?: boolean }) {
  const { me } = useAuth();
  const { t } = useI18n();
  const canBill = (me?.permissions["billing.manage"] ?? "none") !== "none";
  const name = t(feature === "attendance" ? "tw.feat.attendance" : feature === "custom_domain" ? "ty.feat" : "tw.feat.subscriptions");
  const body = feature === "attendance" ? "tw.up.attendance" as const : feature === "custom_domain" ? "ty.up.body" as const : "tw.up.subscriptions" as const;
  // (Task iOS, App Store 3.1.1 / 3.1.3) the store app shows no plan names, prices or upgrade pitch: a neutral note only
  if (!PLAN_PATH) {
    const note = (
      <div className={"card" + (compact ? " upsell-compact" : "")} data-testid="full-plan-upsell" data-feature={feature} data-native="1">
        <h2 className="card-title">{t("native.notEnabled", { feature: name })}</h2>
        <p className="muted">{t("native.askOwner")}</p>
      </div>
    );
    return compact ? note : <div className="page">{note}</div>;
  }
  const points = feature === "attendance" ? (["tw.up.att1", "tw.up.att2", "tw.up.att3"] as const) : feature === "custom_domain" ? (["ty.up.1", "ty.up.2", "ty.up.3"] as const) : (["tw.up.sub1", "tw.up.sub2", "tw.up.sub3"] as const);
  const card = (
    <div className={"card upsell-card" + (compact ? " upsell-compact" : "")} data-testid="full-plan-upsell" data-feature={feature}>
      <span className="upsell-badge">{t("tw.full.badge")}</span>
      <h2 className="card-title">{t("tw.up.title", { feature: name })}</h2>
      <p className="muted">{t(body)}</p>
      <ul className="upsell-points">
        {points.map((k) => <li key={k}>{t(k)}</li>)}
      </ul>
      {PLAN_PATH && canBill
        ? <div className="row-wrap"><Link className="btn btn-primary" to={PLAN_PATH} data-testid="upsell-upgrade">{t("tw.up.cta")}</Link></div>
        : <p className="hint">{PLAN_PATH ? t("tw.up.askOwner") : t("tw.up.native")}</p>}
    </div>
  );
  return compact ? card : <div className="page">{card}</div>;
}
